import { Modal, Radio } from 'semantic-ui-react';
import { ErrorsContextProvider, ValidationErrors } from 'components';
import { SpeakingSessionForLrwSessionDto } from 'api/planning';
import { Footer } from './Footer';
import styled from '@emotion/styled';
import { useLinkExistingSpeakingSession } from './useLinkExistingSpeakingSession';
import { useLrwSessionDetails } from '../details/useLrwSessionDetails';
import { Label } from '@britishcouncil/react-admin';

type Props = {
  availableSpeakingSessions: SpeakingSessionForLrwSessionDto[];
  onCreateNewSpeakingSession: () => void;
};

export const LinkExistingSpeakingSession = ({
  availableSpeakingSessions,
  onCreateNewSpeakingSession,
}: Props) => {
  const {
    selectedSpeakingSession,
    setSelectedSpeakingSession,
    isLinking,
    linked,
    onLink,
    serverErrors,
  } = useLinkExistingSpeakingSession();

  const { lrwSession } = useLrwSessionDetails();

  const options = availableSpeakingSessions.map((x) => (
    <S.Radio
      key={x.id}
      value={x.id}
      onChange={(_, { value }) => value && setSelectedSpeakingSession(+value)}
      checked={selectedSpeakingSession === x.id}
      label={
        <>
          <S.Date>{x.date}</S.Date>
          <S.AdditionalInfo>
            First slot: {x.firstSlotStartTime ?? 'N/A'} | Last slot: {x.lastSlotEndTime ?? 'N/A'} |
            Planned slots: {x.plannedSlots}
          </S.AdditionalInfo>
          <S.VenueRow>
            <S.Venue>{x.venueName}</S.Venue>
            {lrwSession?.venueId === x.venueId && (
              <Label basic color="blue" size="small">
                Same venue as LRW
              </Label>
            )}
          </S.VenueRow>
        </>
      }
    />
  ));

  return (
    <ErrorsContextProvider generalErrors={serverErrors}>
      <Modal open>
        <Modal.Header>Link Speaking session</Modal.Header>
        <Modal.Content scrolling>
          <ValidationErrors />
          <S.Options>{options}</S.Options>
        </Modal.Content>
        <Footer
          submitText="Link"
          isSubmitting={isLinking}
          submitted={linked}
          disabled={!selectedSpeakingSession}
          onSubmit={onLink}
          onCreateNewSpeakingSession={onCreateNewSpeakingSession}
        />
      </Modal>
    </ErrorsContextProvider>
  );
};

const S = {
  Date: styled.div`
    font-weight: bold;
    font-size: 1rem;
    margin-right: auto;
  `,
  VenueRow: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    grid-column: 1 / -1;
    margin-top: 0.5rem;
    min-height: 1.7rem;

    > .label {
      min-width: fit-content;
    }
  `,
  Venue: styled.div`
    font-size: 0.9rem;
  `,
  Radio: styled(Radio)`
    &.ui.checkbox {
      cursor: pointer;
      display: grid;
      grid-template-columns: 50% 50%;
      padding: 1rem;
      width: 100%;
      border: 2px solid #e0e1e2;
      border-radius: 5px;
      &.checked {
        border-color: #2185d0;
        background-color: #e3f0fa;
        &:hover {
          border-color: #1a69a4;
        }
      }
      &:hover {
        border-color: #b2b3b3;
      }
    }
  `,
  Options: styled.div`
    display: flex;
    gap: 1rem;
    flex-direction: column;
  `,
  AdditionalInfo: styled.div`
    margin-left: auto;
    margin-top: 0;
    font-size: 1rem;
    font-weight: 300;
  `,
};
